/**
 * Recently-visited projects — localStorage-backed log of the last 10
 * projects the user opened, ordered most-recent-first. Drives the top
 * "Recently visited" section of the AppShell project switcher.
 *
 * Storage shape:
 *
 *   localStorage['recently-visited-projects'] =
 *     JSON.stringify([{ projectId: string; lastVisitedAt: number }, ...])
 *
 * Capped at 10 entries. New visits move the matching entry to the front
 * with a fresh timestamp; stale entries (project no longer in the
 * caller's project list) are filtered at read-time, not pruned, so a
 * temporarily-403'd or recently-archived project comes back automatically
 * once it's reachable again.
 */

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'recently-visited-projects'
export const MAX_ENTRIES = 10

export interface RecentVisit {
  projectId: string
  /** Epoch ms of the most recent visit. */
  lastVisitedAt: number
}

function isRecentVisit(value: unknown): value is RecentVisit {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return typeof v.projectId === 'string' && typeof v.lastVisitedAt === 'number'
}

/** Read + parse the log. Returns `[]` on any failure (no localStorage,
 *  malformed JSON, missing key). */
export function readRecentVisits(): RecentVisit[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isRecentVisit).slice(0, MAX_ENTRIES)
  } catch {
    return []
  }
}

/** Write the log back to localStorage. Silently no-ops on quota errors. */
function writeRecentVisits(visits: RecentVisit[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visits.slice(0, MAX_ENTRIES)))
  } catch {
    // Quota exceeded or storage disabled — best-effort, drop silently.
  }
}

/**
 * Record a visit for `projectId`. Moves it to the front of the log with
 * a fresh `Date.now()` timestamp; preserves up to `MAX_ENTRIES - 1` of
 * the existing entries behind it.
 *
 * Pure-function form so non-React callers (e.g. a router beforeLoad
 * hook in the future) can reuse the same write without going through
 * the hook.
 */
export function recordProjectVisit(projectId: string): RecentVisit[] {
  if (!projectId) return readRecentVisits()
  const existing = readRecentVisits()
  const without = existing.filter((v) => v.projectId !== projectId)
  const next: RecentVisit[] = [{ projectId, lastVisitedAt: Date.now() }, ...without].slice(
    0,
    MAX_ENTRIES,
  )
  writeRecentVisits(next)
  // Notify same-tab listeners — the native `storage` event only fires
  // across tabs.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('recently-visited-projects-changed'))
  }
  return next
}

/**
 * React hook that returns the current visit log and re-renders when it
 * changes (either via `recordProjectVisit` in this tab or a `storage`
 * event from another tab).
 */
export function useRecentlyVisitedProjects(): RecentVisit[] {
  const [visits, setVisits] = useState<RecentVisit[]>(() => readRecentVisits())

  useEffect(() => {
    const refresh = () => setVisits(readRecentVisits())
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) refresh()
    }
    window.addEventListener('storage', handleStorage)
    window.addEventListener('recently-visited-projects-changed', refresh)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('recently-visited-projects-changed', refresh)
    }
  }, [])

  return visits
}

/**
 * Imperative recorder hook — wraps `recordProjectVisit` in a stable
 * callback so callers can `useEffect(() => record(projectId), [...])`.
 */
export function useRecordProjectVisit(): (projectId: string) => void {
  return useCallback((projectId: string) => {
    recordProjectVisit(projectId)
  }, [])
}
